import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import type { DocumentReferencesView } from 'api';
import { ApiService, CompanyService, RetrieveDocumentGenerationJob } from 'api';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { EventType, useEventBus } from 'context';
import { useAsyncProcessor } from 'context/AsyncProcessorContext/AsyncProcessorContext';
import { AIGradientButton } from 'pages/DocumentGenerationJobs/components/AIGradient';
import { useGetCaptableInfo } from 'services/hooks/captable';
import { extractResponseErrorMessage, Logger } from 'utilities';

interface CreateCapTableCitationsDialogProps {
  documents: DocumentReferencesView[];
  onClose: () => void;
  open: boolean;
}

const documentDisplayName = (document: DocumentReferencesView) => {
  if (!document.file) {
    return 'Unknown file';
  }
  const { filename, file_type: fileType } = document.file;
  // Stored filenames sometimes already carry the extension; only append it when missing.
  const suffix = `.${fileType}`;
  return filename.toLowerCase().endsWith(suffix.toLowerCase()) ? filename : `${filename}${suffix}`;
};

/**
 * Confirm dialog for the citations-only run: cites the CURRENT cap-table values
 * against the referenced documents. Values never change; on completion the same
 * apply endpoint as the generate flow runs (the backend routes on job metadata)
 * and the cap table is refetched so the citation badges re-render.
 */
const CreateCapTableCitationsDialog = ({ documents, onClose, open }: CreateCapTableCitationsDialogProps) => {
  const [{ captableInfo, companyInfo }] = useStore() as unknown as UseStoreValues;
  const [, getCapTableInfo] = useGetCaptableInfo();
  const { addJob, removeJob } = useAsyncProcessor();
  const { publish, subscribe } = useEventBus();
  const [isStarting, setIsStarting] = useState(false);

  // Refresh the currently viewed cap-table version (and therefore the citation
  // badges) whenever a generation job completes - same wiring as GenerateCapTableJob.
  useEffect(() => {
    const unsubscribe = subscribe(EventType.CAP_TABLE_GENERATION_JOB_COMPLETED, () => {
      if (captableInfo?.id) {
        (getCapTableInfo as (id?: number) => Promise<void>)(captableInfo.id);
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captableInfo?.id, subscribe]);

  const handleConfirm = async () => {
    if (!companyInfo?.id || !captableInfo?.id || documents.length === 0) {
      return;
    }
    setIsStarting(true);
    onClose();

    const companyIdStr = companyInfo.id.toString();

    try {
      const newJob = await CompanyService.apiCompanyCapTablesCitationsGenerationJobCreate(companyIdStr, {
        cap_table_id: captableInfo.id,
      });

      addJob({
        id: newJob.job_id,
        details: {
          title: 'Creating Citations',
          description: `Citing ${documents.length} document${documents.length > 1 ? 's' : ''}`,
        },
        pollingConfig: {
          pollingFunction: async () => CompanyService.apiCompanyDocumentGenerationJobsRead(companyIdStr, newJob.job_id),
          pollingInterval: 3000,
          pollingArgs: [],
        },
        onResult: async (result: RetrieveDocumentGenerationJob) => {
          if (result.status === RetrieveDocumentGenerationJob.status.COMPLETED) {
            try {
              const applyResult = await ApiService.apiCompanyCapTablesApplyGenerationJobCreate(companyIdStr, {
                job_id: result.job_id,
              });

              const count = applyResult?.security_ids?.length ?? 0;
              publish(EventType.CAP_TABLE_GENERATION_JOB_COMPLETED, result.job_id);
              enqueueSnackbar(
                count > 0
                  ? `Citations refreshed for ${count} ${count > 1 ? 'securities' : 'security'}`
                  : 'Citation generation completed',
                { variant: 'success' }
              );
            } catch (applyError) {
              Logger.error('CreateCapTableCitationsDialog - Error applying citations job', applyError);
              enqueueSnackbar('Job completed but applying citations to the cap table failed', {
                variant: 'warning',
              });
            }
            removeJob(result.job_id);
          } else if (result.status === RetrieveDocumentGenerationJob.status.FAILED) {
            enqueueSnackbar('Citation generation job failed', { variant: 'error' });
            removeJob(result.job_id);
          }
        },
        onError: (error: Error) => {
          Logger.error('CreateCapTableCitationsDialog - Citations job polling error', error);
        },
      });

      enqueueSnackbar('Citation generation started. Citation badges will refresh when the job completes.', {
        variant: 'info',
      });
    } catch (error) {
      Logger.error('CreateCapTableCitationsDialog - Error creating citations job', error);
      const errorMessage = extractResponseErrorMessage(error) ?? 'Failed to start citation generation';
      enqueueSnackbar(`Error: ${errorMessage}`, { variant: 'error' });
    } finally {
      setIsStarting(false);
    }
  };

  const showVersionNote = captableInfo?.is_primary === false && Boolean(captableInfo?.name);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Citations</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {`Create citations for the current cap table using ${
            documents.length === 1 ? 'this document' : `these ${documents.length} documents`
          }? Values will not change; citation badges will refresh with the new evidence.`}
        </Typography>
        <List dense>
          {documents.map(document => (
            <ListItem key={document.document_reference_id} disableGutters>
              <ListItemText primary={documentDisplayName(document)} />
            </ListItem>
          ))}
        </List>
        {showVersionNote && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {`Citations will be created for the "${captableInfo?.name}" cap table version.`}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <AIGradientButton onClick={handleConfirm} disabled={isStarting || documents.length === 0}>
          Create Citations
        </AIGradientButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCapTableCitationsDialog;
